import { Link } from 'react-router-dom';
import { FileText, Mail } from 'lucide-react';
import PageLayout from '../components/PageLayout';

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-xl font-bold text-white mb-4 pb-3 border-b border-white/[0.06]">
        {title}
      </h2>
      <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function Sub({ title, children }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-slate-200 mb-2">{title}</h3>
      <div className="space-y-3 text-slate-400 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Ul({ items }) {
  return (
    <ul className="space-y-1.5 pl-4">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-slate-400 text-sm">
          <span className="text-[#00E5FF] mt-1 flex-shrink-0">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

const TOC = [
  { id: 'acceptance',      label: '1. Acceptance of Terms' },
  { id: 'description',     label: '2. Service Description' },
  { id: 'accounts',        label: '3. Accounts & Registration' },
  { id: 'acceptable-use',  label: '4. Acceptable Use' },
  { id: 'prohibited',      label: '5. Prohibited Uses' },
  { id: 'api',             label: '6. API & Developer Terms' },
  { id: 'payment',         label: '7. Payment & Billing' },
  { id: 'ip',              label: '8. Intellectual Property' },
  { id: 'disclaimer',      label: '9. Disclaimer of Warranties' },
  { id: 'liability',       label: '10. Limitation of Liability' },
  { id: 'indemnification', label: '11. Indemnification' },
  { id: 'termination',     label: '12. Termination' },
  { id: 'governing-law',   label: '13. Governing Law' },
  { id: 'changes',         label: '14. Changes to Terms' },
  { id: 'contact',         label: '15. Contact' },
];

export default function TermsOfService() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PageLayout
      badge="Legal"
      badgeColor="purple"
      title="Terms of Service"
      subtitle="The rules that govern your use of the ZenithCipher platform and API."
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex gap-14">

          {/* Sticky TOC */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-20">
              <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-3">Contents</div>
              <nav className="space-y-0.5">
                {TOC.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => scrollTo(id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs text-slate-500 hover:text-[#00E5FF] hover:bg-[#00E5FF]/5 transition-all"
                  >
                    {label}
                  </button>
                ))}
              </nav>
              <div className="mt-6 p-4 glass-card border border-white/[0.06] rounded-xl text-center">
                <FileText size={16} className="text-[#7C3AED] mx-auto mb-2" />
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  Last updated<br />
                  <span className="text-slate-300 font-medium">May 1, 2026</span>
                </p>
              </div>
            </div>
          </aside>

          {/* Content */}
          <article className="flex-1 min-w-0">

            <div className="mb-10 p-5 glass-card border border-[#7C3AED]/15 rounded-xl text-sm text-slate-400 leading-relaxed">
              <strong className="text-white">Effective date:</strong> May 1, 2026. &nbsp;
              These Terms of Service ("Terms") constitute a legally binding agreement between you and{' '}
              <strong className="text-white">ZenithCipher, Inc.</strong> ("ZenithCipher", "we", "us", "our").
              Please read them carefully before using our Services. If you do not agree, do not use the Services.
            </div>

            <Section id="acceptance" title="1. Acceptance of Terms">
              <p>
                By accessing or using ZenithCipher's website, dashboard, or API (collectively, the "Services"),
                you confirm that you are at least 18 years old, have the legal capacity to enter into this
                agreement, and agree to be bound by these Terms and our{' '}
                <Link to="/privacy" className="text-[#00E5FF] hover:underline">Privacy Policy</Link>.
              </p>
              <p>
                If you are using the Services on behalf of an organization, you represent and warrant that
                you have authority to bind that organization to these Terms.
              </p>
            </Section>

            <Section id="description" title="2. Service Description">
              <p>
                ZenithCipher provides a blockchain intelligence and wallet risk analysis platform, including:
              </p>
              <Ul items={[
                'A web dashboard for scanning Ethereum wallet addresses',
                'A REST API for programmatic wallet risk analysis',
                'Scan history storage and report export functionality',
                'API key management for developer integrations',
              ]} />
              <p>
                <strong className="text-white">Advisory nature:</strong> ZenithCipher's risk scores and
                analysis are <em>informational and advisory only</em>. They are not legal advice, financial
                advice, compliance certifications, or definitive determinations of fraud or criminal activity.
                You are solely responsible for your own business decisions based on our outputs.
              </p>
            </Section>

            <Section id="accounts" title="3. Accounts &amp; Registration">
              <Sub title="3.1 Account Creation">
                <p>
                  You must provide a valid email address and create a secure password. You are responsible
                  for maintaining the confidentiality of your credentials and all activity under your account.
                </p>
              </Sub>
              <Sub title="3.2 Account Security">
                <p>
                  You must notify us immediately at{' '}
                  <a href="mailto:support@zenithcipher.io" className="text-[#00E5FF] hover:underline">
                    support@zenithcipher.io
                  </a>{' '}
                  if you become aware of any unauthorized use of your account. We are not liable for
                  losses resulting from unauthorized account access due to your failure to keep credentials secure.
                </p>
              </Sub>
              <Sub title="3.3 One Account Per Person">
                <p>
                  Each individual may maintain only one free account. Creating multiple accounts to circumvent
                  rate limits or plan restrictions is a violation of these Terms and may result in account termination.
                </p>
              </Sub>
            </Section>

            <Section id="acceptable-use" title="4. Acceptable Use">
              <p>You may use the Services only for lawful purposes and in accordance with these Terms. Permitted uses include:</p>
              <Ul items={[
                'Analyzing Ethereum wallet addresses for legitimate security, compliance, or due diligence purposes',
                'Integrating our API into your application, exchange, or compliance workflow',
                'Research into blockchain fraud patterns and on-chain security',
                'Internal security operations and risk management',
              ]} />
            </Section>

            <Section id="prohibited" title="5. Prohibited Uses">
              <p>You agree not to use the Services to:</p>
              <Ul items={[
                'Violate any applicable law, regulation, or third-party rights',
                'Harass, threaten, or harm individuals based on wallet analysis results',
                'Attempt to reverse-engineer, decompile, or extract our risk scoring algorithms',
                'Scrape, bulk-download, or resell our data or API outputs without prior written consent',
                'Circumvent rate limits, IP restrictions, or authentication mechanisms',
                'Use the API to provide a competing wallet intelligence service to third parties',
                'Submit false, fabricated, or misleading wallet addresses to manipulate our systems',
                'Attempt to disrupt, attack, or impair the performance of the Services',
                'Use the Services in connection with money laundering, terrorism financing, or sanctions evasion',
              ]} />
              <p>
                Violation of these prohibited use provisions may result in immediate account termination and
                may be reported to relevant law enforcement authorities.
              </p>
            </Section>

            <Section id="api" title="6. API &amp; Developer Terms">
              <Sub title="6.1 API Keys">
                <p>
                  API keys are issued per account and plan tier. Keys must not be shared publicly (e.g.,
                  in public GitHub repositories, client-side JavaScript). You are responsible for all API
                  calls made using your key.
                </p>
              </Sub>
              <Sub title="6.2 Rate Limits">
                <p>
                  API usage is subject to rate limits as described in our{' '}
                  <Link to="/docs#rate-limits" className="text-[#00E5FF] hover:underline">documentation</Link>.
                  Exceeding rate limits will result in temporary request blocking. Systematic circumvention of
                  rate limits is a violation of these Terms.
                </p>
              </Sub>
              <Sub title="6.3 API Changes">
                <p>
                  We may modify, deprecate, or remove API endpoints with at least 30 days notice for
                  breaking changes, except in cases of security vulnerabilities or legal requirements.
                </p>
              </Sub>
              <Sub title="6.4 Data Accuracy">
                <p>
                  Our API results depend on third-party data sources including Etherscan. We do not
                  guarantee the accuracy, completeness, or timeliness of blockchain data returned.
                </p>
              </Sub>
            </Section>

            <Section id="payment" title="7. Payment &amp; Billing">
              <Sub title="7.1 Plans">
                <p>
                  We offer Free, Starter, Pro, and Enterprise plans. Paid plans are billed monthly in
                  advance. Prices are listed on our{' '}
                  <Link to="/#pricing" className="text-[#00E5FF] hover:underline">Pricing page</Link> and may
                  change with 30 days advance notice.
                </p>
              </Sub>
              <Sub title="7.2 Payment Processing">
                <p>
                  Payments are processed by Stripe. By providing payment information, you authorize us
                  to charge your payment method on a recurring monthly basis until you cancel.
                </p>
              </Sub>
              <Sub title="7.3 Refunds">
                <p>
                  We offer a 14-day money-back guarantee for new paid subscriptions. After 14 days,
                  payments are non-refundable except where required by applicable law.
                </p>
              </Sub>
              <Sub title="7.4 Cancellation">
                <p>
                  You may cancel your subscription at any time from Dashboard → Billing. Cancellation
                  takes effect at the end of the current billing period. You retain access until then.
                </p>
              </Sub>
            </Section>

            <Section id="ip" title="8. Intellectual Property">
              <p>
                All content, design, algorithms, software, trademarks, and trade secrets comprising the
                ZenithCipher Services are and remain the exclusive property of ZenithCipher, Inc. and
                its licensors. These Terms do not grant you any ownership interest in the Services.
              </p>
              <p>
                <strong className="text-white">Your data:</strong> You retain full ownership of your
                scan history and any data you submit to the Services. You grant ZenithCipher a limited
                license to process and store this data solely to provide the Services to you.
              </p>
              <p>
                <strong className="text-white">Feedback:</strong> If you submit feature suggestions or
                feedback, you grant ZenithCipher a royalty-free, irrevocable right to use that feedback
                without restriction or compensation.
              </p>
            </Section>

            <Section id="disclaimer" title="9. Disclaimer of Warranties">
              <p className="uppercase text-xs leading-relaxed">
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS
                FOR A PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT.
              </p>
              <p>
                We do not warrant that the Services will be uninterrupted, error-free, or that any
                particular wallet scan result is accurate, complete, or free from false positives or
                false negatives. Risk scores are probabilistic assessments, not definitive determinations.
              </p>
            </Section>

            <Section id="liability" title="10. Limitation of Liability">
              <p className="uppercase text-xs leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ZENITHCIPHER AND ITS OFFICERS,
                DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA,
                GOODWILL, OR BUSINESS INTERRUPTION, ARISING OUT OF OR IN CONNECTION WITH YOUR USE
                OF THE SERVICES.
              </p>
              <p>
                IN NO EVENT SHALL ZENITHCIPHER'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS RELATED
                TO THE SERVICES EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO ZENITHCIPHER
                IN THE 12 MONTHS PRECEDING THE CLAIM, OR (B) $100 USD.
              </p>
            </Section>

            <Section id="indemnification" title="11. Indemnification">
              <p>
                You agree to indemnify, defend, and hold harmless ZenithCipher and its officers,
                directors, employees, and agents from any claims, losses, damages, liabilities, and
                expenses (including reasonable attorneys' fees) arising out of your use of the Services,
                your violation of these Terms, or your violation of any third-party rights.
              </p>
            </Section>

            <Section id="termination" title="12. Termination">
              <p>
                We reserve the right to suspend or terminate your account and access to the Services
                at any time, with or without notice, for violations of these Terms, illegal conduct,
                or any reason we deem necessary to protect the Services or other users.
              </p>
              <p>
                You may terminate your account at any time by contacting support or using the account
                deletion option in Dashboard → Settings. Upon termination, your data will be deleted
                within 30 days.
              </p>
              <p>
                Sections covering intellectual property, disclaimer of warranties, limitation of
                liability, indemnification, and governing law survive termination of these Terms.
              </p>
            </Section>

            <Section id="governing-law" title="13. Governing Law &amp; Disputes">
              <p>
                These Terms are governed by the laws of the State of California, United States,
                without regard to conflict of law principles. Any disputes shall be resolved exclusively
                in the state or federal courts located in San Francisco County, California.
              </p>
              <p>
                <strong className="text-white">Informal resolution:</strong> Before filing any legal
                claim, you agree to first attempt to resolve the dispute informally by contacting us
                at{' '}
                <a href="mailto:legal@zenithcipher.io" className="text-[#00E5FF] hover:underline">
                  legal@zenithcipher.io
                </a>.
                We will attempt to resolve the dispute within 30 days.
              </p>
            </Section>

            <Section id="changes" title="14. Changes to These Terms">
              <p>
                We may update these Terms from time to time. We will notify you of material changes
                by email (at least 14 days in advance) and by updating the "Effective date" above.
                Continued use of the Services after changes take effect constitutes acceptance of the
                updated Terms.
              </p>
            </Section>

            <Section id="contact" title="15. Contact">
              <p>Questions about these Terms? Contact our Legal Team:</p>
              <div className="mt-4 p-5 glass-card border border-white/[0.06] rounded-xl flex items-start gap-4">
                <div className="w-9 h-9 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center flex-shrink-0">
                  <Mail size={15} className="text-[#7C3AED]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white mb-0.5">ZenithCipher Legal Team</div>
                  <a href="mailto:legal@zenithcipher.io" className="text-[#00E5FF] text-sm hover:underline">
                    legal@zenithcipher.io
                  </a>
                  <div className="text-xs text-slate-500 mt-1">ZenithCipher, Inc. · San Francisco, CA</div>
                </div>
              </div>
            </Section>

          </article>
        </div>
      </div>
    </PageLayout>
  );
}
